import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Alert,
    InputAdornment,
} from '@mui/material';
import { useTranslate, useDataProvider, useNotify } from 'react-admin';
import { OnboardingData } from '../CompanyOnboardingWizard';

interface Props {
    data: OnboardingData;
    updateData: (updates: Partial<OnboardingData>) => void;
    errors: Record<string, string>;
}

interface CompanyOption {
    id: number;
    name: string;
}

export const DemoDataStep: React.FC<Props> = ({ data, updateData, errors }) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [templates, setTemplates] = useState<CompanyOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                // Filter auf isDemoTemplate=true (Backend-Field aus Issue #269)
                const result = await dataProvider.getList('companies', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'name', order: 'ASC' },
                    filter: { isDemoTemplate: true, active: true },
                });
                if (!cancelled) {
                    setTemplates((result.data as any[]).map(c => ({ id: c.id, name: c.name })));
                }
            } catch {
                if (!cancelled) {
                    notify(translate('companyOnboarding.errors.demoTemplatesLoad'), { type: 'warning' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [dataProvider, notify, translate]);

    const updateSelection = (key: keyof OnboardingData['demoSelection'], checked: boolean) => {
        updateData({ demoSelection: { ...data.demoSelection, [key]: checked } });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.demoDataTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.demoDataDescription')}
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <Autocomplete
                        options={templates}
                        getOptionLabel={(opt) => opt.name}
                        value={templates.find(t => t.id === data.demoSourceCompanyId) ?? null}
                        onChange={(_, opt) => updateData({ demoSourceCompanyId: opt ? opt.id : null })}
                        loading={loading}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={translate('companyOnboarding.fields.demoSourceCompany')}
                                required
                                error={!!errors.demoSourceCompanyId}
                                helperText={errors.demoSourceCompanyId || translate('companyOnboarding.fields.demoSourceCompanyHelp')}
                            />
                        )}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label={translate('companyOnboarding.fields.demoExpiresInDays')}
                        type="number"
                        value={data.demoExpiresInDays}
                        onChange={(e) => updateData({ demoExpiresInDays: parseInt(e.target.value) || 0 })}
                        fullWidth
                        required
                        error={!!errors.demoExpiresInDays}
                        helperText={errors.demoExpiresInDays || translate('companyOnboarding.fields.demoExpiresInDaysHelp')}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">{translate('companyOnboarding.units.days')}</InputAdornment>,
                            inputProps: { min: 1, max: 365 },
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        {translate('companyOnboarding.fields.demoSelectionTitle')}
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.catalogs} onChange={(e) => updateSelection('catalogs', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.catalogs')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.courses} onChange={(e) => updateSelection('courses', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.courses')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.learningPaths} onChange={(e) => updateSelection('learningPaths', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.learningPaths')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.wiki} onChange={(e) => updateSelection('wiki', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.wiki')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.media} onChange={(e) => updateSelection('media', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.media')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={data.demoSelection.h5p} onChange={(e) => updateSelection('h5p', e.target.checked)} />}
                            label={translate('companyOnboarding.fields.demoSelection.h5p')}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 3 }}>
                {translate('companyOnboarding.warnings.demoSeedDuration')}
            </Alert>
        </Box>
    );
};
